import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { Stack } from '@fluentui/react';
import './TiptapEditor.css';

interface TiptapEditorProps {
	value: string;
	onChange: (content: string) => void;
}

const TiptapEditor: React.FC<TiptapEditorProps> = ({ value, onChange }) => {
	const [showHtml, setShowHtml] = useState(false);
	const [htmlContent, setHtmlContent] = useState('');
	const editor = useEditor({
		extensions: [
			StarterKit,
			Link.configure({
				openOnClick: false,
			}),
			TextAlign.configure({
				types: ['heading', 'paragraph'],
			}),
			Underline,
		],
		content: value,
		onUpdate: ({ editor }: { editor: any }) => {
			onChange(editor.getHTML());
		},
		editorProps: {
			attributes: {
				class: 'tiptap-editor',
			},
		},
	});

	React.useEffect(() => {
		if (editor && value !== editor.getHTML()) {
			const { from, to } = editor.state.selection;
			editor.commands.setContent(value, { emitUpdate: false });
			editor.commands.setTextSelection({ from, to });
		}
		// Update htmlContent when value prop changes
		setHtmlContent(value);
	}, [value, editor]);

	if (!editor) {
		return null;
	}

	const toggleHtmlMode = () => {
		if (showHtml) {
			// Switching from HTML to visual mode
			// Update editor with the HTML content
			editor.commands.setContent(htmlContent, { emitUpdate: false });
			// Trigger onChange with the editor's processed HTML to ensure validity
			setTimeout(() => {
				onChange(editor.getHTML());
			}, 0);
		} else {
			// Switching from visual to HTML mode
			// Update htmlContent with current editor content
			setHtmlContent(editor.getHTML());
		}
		setShowHtml(!showHtml);
	};

	const handleHtmlChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const newHtml = e.target.value;
		setHtmlContent(newHtml);
		onChange(newHtml);
	};

	const MenuBar = () => {
		return (
			<div className="tiptap-menubar">
				<button
					onClick={() => editor.chain().focus().toggleBold().run()}
					className={editor.isActive('bold') ? 'is-active' : ''}
					type="button"
					disabled={showHtml}
				>
					Bold
				</button>
				<button
					onClick={() => editor.chain().focus().toggleItalic().run()}
					className={editor.isActive('italic') ? 'is-active' : ''}
					type="button"
					disabled={showHtml}
				>
					Italic
				</button>
				<button
					onClick={() => editor.chain().focus().toggleUnderline().run()}
					className={editor.isActive('underline') ? 'is-active' : ''}
					type="button"
					disabled={showHtml}
				>
					Underline
				</button>
				<span className="separator">|</span>
				<button
					onClick={() => editor.chain().focus().setTextAlign('left').run()}
					className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
					type="button"
					disabled={showHtml}
				>
					Left
				</button>
				<button
					onClick={() => editor.chain().focus().setTextAlign('center').run()}
					className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
					type="button"
					disabled={showHtml}
				>
					Center
				</button>
				<button
					onClick={() => editor.chain().focus().setTextAlign('right').run()}
					className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
					type="button"
					disabled={showHtml}
				>
					Right
				</button>
				<button
					onClick={() => editor.chain().focus().setTextAlign('justify').run()}
					className={editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}
					type="button"
					disabled={showHtml}
				>
					Justify
				</button>
				<span className="separator">|</span>
				<button
					onClick={() => editor.chain().focus().toggleBulletList().run()}
					className={editor.isActive('bulletList') ? 'is-active' : ''}
					type="button"
					disabled={showHtml}
				>
					Bullet List
				</button>
				<button
					onClick={() => editor.chain().focus().toggleOrderedList().run()}
					className={editor.isActive('orderedList') ? 'is-active' : ''}
					type="button"
					disabled={showHtml}
				>
					Numbered List
				</button>
				<span className="separator">|</span>
				<button
					onClick={() => editor.chain().focus().undo().run()}
					disabled={!editor.can().undo() || showHtml}
					type="button"
				>
					Undo
				</button>
				<button
					onClick={() => editor.chain().focus().redo().run()}
					disabled={!editor.can().redo() || showHtml}
					type="button"
				>
					Redo
				</button>
				<span className="separator">|</span>
				<button
					onClick={toggleHtmlMode}
					className={showHtml ? 'is-active' : ''}
					type="button"
				>
					{showHtml ? 'Visual Editor' : 'View Source'}
				</button>
			</div>
		);
	};

	return (
		<Stack className="tiptap-wrapper">
			<MenuBar />
			{showHtml ? (
				<textarea
					className="tiptap-html-editor"
					value={htmlContent}
					onChange={handleHtmlChange}
					spellCheck={false}
				/>
			) : (
				<EditorContent editor={editor} />
			)}
		</Stack>
	);
};

export default TiptapEditor;
